import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { productData } from "../common/globalapi/Api";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import moment from "moment";

const ViewProduct = () => {
    const navigate = useNavigate();
    const {
        control,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const [searchTerm, setSearchTerm] = useState("");

    let role = sessionStorage.getItem("rolebase");
    let userId = sessionStorage.getItem("userId");
    const [selectedRows, setSelectedRows] = useState([]);
    const fromDate = watch("fromDate");
    const toDate = watch("toDate");

    // GET API START
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["productData", userId, true],
        queryFn: () => productData(userId, true),
        enabled: !!userId,
        refetchOnWindowFocus: false,
    });

    let details = (data && data?.data) || [];
    const uniqueCategories = [...new Set(details?.map((item) => item?.category).filter(Boolean))];

    // Filter By search
    const filteredDetails = details?.filter((item) => {
        const lowerSearch = searchTerm?.toLowerCase() || "";

        const nameMatch =
            !lowerSearch ||
            item?.productName?.toLowerCase().includes(lowerSearch);

        const status = watch("status")?.toUpperCase();
        const stock = item?.stockStatus?.toUpperCase();

        const statusMatch = !status || stock === status || (status === "OUT OF STOCK" && Number(item?.quantity) === 0) || (status === "AVAILABLE" && stock === "IN STOCK");

        // Convert selected range to timestamps
        const from = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
        const to = toDate ? new Date(toDate).setHours(23, 59, 59, 999) : null;

        // Parse item dates
        const manufacturing = item?.manufacturingDate ? new Date(item.manufacturingDate).getTime() : null;

        const expiry = item?.expiryDate ? new Date(item.expiryDate).getTime() : null;

        // Date match: either date falls inside the selected range
        const dateMatch = !from || !to || (manufacturing && manufacturing >= from && manufacturing <= to) || (expiry && expiry >= from && expiry <= to);

        const selectedCategory = watch("category");
        const categoryMatch = !selectedCategory || item?.category === selectedCategory;

        return nameMatch && statusMatch && dateMatch && categoryMatch;
    });

    // Dashboard Report
    let overallproduct = filteredDetails && filteredDetails?.length;
    const availableCount = filteredDetails && filteredDetails?.filter((item) => {
        const s = (item?.stockStatus || "").toLowerCase();
        return s === "in stock" || s === "available";
    }).length;

    const outStockCount = filteredDetails?.filter((item) => {
        const s = (item?.stockStatus || "").toLowerCase();
        return s === "out of stock" || Number(item?.quantity) === 0;
    }).length || 0;

    // DELETE API
    const deleteUser = async (item) => {
        // Resolve registerId with safe fallbacks to avoid `undefined` in URL
        const companyDetails = (() => {
            try {
                return JSON.parse(localStorage.getItem("companyDetails") || "{}") || {};
            } catch (e) {
                return {};
            }
        })();

        // Prefer session user id as register identifier when available
        const regId = item?.registerId || userId || companyDetails?.registerId || "";

        if (!regId) {
            const err = new Error("Missing registerId for delete request");
            console.error(err);
            throw err;
        }

        try {
            const url = `${process.env.REACT_APP_HOST}/api/Products/${regId}/${item.id}`;
            const response = await axios.delete(url);
            return response.data;
        } catch (error) {
            console.error(`Error deleting product id=${item.id} (registerId=${regId}):`, error);
            throw error;
        }
    };

    const deleteUsers = async (items) => {
        try {
            const results = await Promise.all(
                items?.map(async (item) => {
                    return await deleteUser(item);
                })
            );
            return results;
        } catch (error) {
            console.error("Error deleting multiple users:", error.message);
            throw new Error("Failed to delete multiple users");
        }
    };

    // Mutation to delete users
    const { mutate, isPending: deleteLoading } = useMutation({
        mutationFn: deleteUsers,
        onSuccess: () => {
            toast.success("Products Deleted Successfully!");
            setSelectedRows([]);
            refetch();
        },
        onError: (error) => {
            console.error("Error deleting rows:", error);
            toast.error("Error deleting rows");
        },
    });

    // Handle checkbox change
    const handleCheckboxChange = (item) => {
        setSelectedRows((prevSelectedRows) => {
            const isSelected = prevSelectedRows?.some(row => row.id === item.id);
            if (isSelected) {
                return prevSelectedRows?.filter((row) => row.id !== item.id);
            } else {
                return [...prevSelectedRows, item];
            }
        });
    };
    // Handle delete button click
    const handleDelete = () => {
        if (selectedRows?.length === 0) {
            alert("No rows selected");
            return;
        }
        mutate(selectedRows);
    };

    // DELETE API END

    // Download Excel
    const downloadExcel = () => {
        // Define headers
        const headers = [
            "S.NO",
            "PRODUCT NAME",
            "UNIT PRICE",
            "QUANTITY",
            "QUANTITY TYPE",
            "STOCK STATUS",
        ];

        // Map data to match the headers order
        const formattedData =
            filteredDetails &&
            filteredDetails?.map((item, index) => ({
                "S.NO": index + 1,
                "PRODUCT NAME": item?.productName || "",
                "UNIT PRICE": item?.unitPrice || "",
                QUANTITY: item?.quantity || "",
                "QUANTITY TYPE": item?.quantityType || "",
                "STOCK STATUS": item?.stockStatus || "",
            }));

        // Create a new workbook and a worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });

        // Append the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

        // Generate Excel file and trigger download
        XLSX.writeFile(wb, "View Product.xlsx");
    };

    return (
        <>
            <div className="view_form_block">
                <div className="view_form_sub_block">
                    {/* DASHBOARD */}
                    <div className="row my-3 d-flex justify-content-center">
                        <div className="col-12 col-sm-6 col-md-3 col-lg-2 dashborad-card-block --first-block">
                            <span className="dashboard-heading" style={{ color: "blue" }}>
                                Overall
                            </span>
                            <span className="dashboard-value" style={{ color: "#1E3A8A" }}>
                                {overallproduct || 0}
                            </span>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-2 dashborad-card-block">
                            <span className="dashboard-heading" style={{ color: "green" }}>
                                Available
                            </span>
                            <span className="dashboard-value" style={{ color: "#047857" }}>
                                {availableCount || 0}
                            </span>
                        </div>

                        <div className="col-12 col-sm-6 col-md-3 col-lg-2 dashborad-card-block --last-block">
                            <span className="dashboard-heading" style={{ color: "red" }}>
                                Out Of Stock
                            </span>
                            <span className="dashboard-value" style={{ color: "#B91C1C" }}>
                                {outStockCount || 0}
                            </span>
                        </div>
                    </div>

                    {/* Filters and Actions Row */}
                    {/* Filter Card */}
                    <div className="card border-0 shadow-sm p-3 mb-4 bg-white">
                        <div className="row g-3 align-items-center">
                            {/* From Date */}
                            <div className="col-auto w-100-mobile">
                                <Controller
                                    control={control}
                                    name="fromDate"
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            selected={field.value}
                                            onChange={field.onChange}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="From Date"
                                            maxDate={new Date()}
                                            className="filter_date_picker"
                                        />
                                    )}
                                />
                            </div>

                            {/* To Date */}
                            <div className="col-auto w-100-mobile">
                                <Controller
                                    control={control}
                                    name="toDate"
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            selected={field.value}
                                            onChange={field.onChange}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="To Date"
                                            minDate={fromDate}
                                            maxDate={new Date()}
                                            className="filter_date_picker"
                                        />
                                    )}
                                />
                            </div>

                            {/* Search */}
                            <div className="col-auto flex-grow-1">
                                <input
                                    type="search"
                                    className="view_table_search w-100"
                                    placeholder="Search by product Name"
                                    title="Search by product Name"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Status */}
                            <div className="col-auto w-100-mobile">
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <select {...field} className="view-table-status">
                                            <option value="">STOCK STATUS</option>
                                            <option value="AVAILABLE">AVAILABLE</option>
                                            <option value="OUT OF STOCK">OUT OF STOCK</option>
                                        </select>
                                    )}
                                />
                            </div>

                            {/* Category */}
                            <div className="col-auto w-100-mobile">
                                <Controller
                                    name="category"
                                    control={control}
                                    render={({ field }) => (
                                        <select {...field} className="view-table-status">
                                            <option value="">ALL CATEGORIES</option>
                                            {uniqueCategories.map((cat, idx) => (
                                                <option key={idx} value={cat}>
                                                    {cat}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                />
                            </div>

                            {/* Actions */}
                            <div className="col-auto d-flex gap-3 w-100-mobile justify-content-center justify-content-md-start flex-wrap">
                                <div
                                    className="download_btn_block_purchase"
                                    onClick={() => navigate("/addProduct")}
                                >
                                    <button className="download_btn">NEW PRODUCT</button>
                                </div>
                                <div
                                    className="download_btn_block_purchase"
                                    onClick={downloadExcel}
                                >
                                    <div className="download_btn">DOWNLOAD</div>
                                </div>
                                {selectedRows?.length !== 0 && (
                                    <div
                                        className="download_btn_block_purchase"
                                        onClick={() => {
                                            if (window.confirm("Are you sure you want to delete the product details?")) {
                                                handleDelete();
                                            }
                                        }}
                                    >
                                        <button className="download_btn">DELETE</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row px-3 my-3">
                        <div className="col-lg-12 view_table_section">
                            <div className="table-responsive view_table_block">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th className="view-table-heading">SELECT</th>
                                            <th className="view-table-heading">S.NO</th>
                                            <th className="view-table-heading">PRODUCT NAME</th>
                                            <th className="view-table-heading">CATEGORY</th>
                                            <th className="view-table-heading">PURCHASE PRICE</th>
                                            <th className="view-table-heading">QUANTITY</th>
                                            <th className="view-table-heading">QUANTITY TYPE</th>
                                            <th className="view-table-heading">STOCK STATUS</th>
                                        </tr>
                                    </thead>
                                    <tbody>

                                        {isLoading ? (
                                            <>
                                                <tr>
                                                    <td colSpan={8} className="text-center">

                                                        <h5 className="my-3">L O A D I N G . . .</h5>
                                                    </td>
                                                </tr>
                                            </>
                                        ) : filteredDetails?.length > 0 ? (
                                            <>
                                                {filteredDetails?.map((data, i) => {
                                                    return (
                                                        <>
                                                            <tr
                                                                key={i}
                                                                onClick={() => {
                                                                    if (role !== "Sales") {
                                                                        navigate("/addProduct", { state: data });
                                                                    }
                                                                }}
                                                                style={{
                                                                    cursor:
                                                                        role !== "Sales" ? "pointer" : "default",
                                                                }}
                                                            >
                                                                <td
                                                                    className="view-table-data"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        style={{
                                                                            accentColor: "blue",
                                                                            width: "13px",
                                                                            height: "13px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                        checked={selectedRows?.some(row => row.id === data?.id)}
                                                                        onChange={() =>
                                                                            handleCheckboxChange(data)
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="view-table-data">
                                                                    {i + 1}
                                                                </td>
                                                                <td className="view-table-data">
                                                                    {data?.productName || "-"}
                                                                </td>
                                                                <td className="view-table-data">
                                                                    {data?.category || "-"}
                                                                </td>
                                                                <td className="view-table-data">
                                                                    {data?.purchasePrice ?? "-"}
                                                                </td>
                                                                <td className="view-table-data">
                                                                    {data?.quantity ?? "-"}
                                                                </td>
                                                                <td className="view-table-data">
                                                                    {data?.quantityType || "-"}
                                                                </td>
                                                                <td className="view-table-data">
                                                                    {data?.quantity == 0
                                                                        ? "Out Of Stock"
                                                                        : data?.stockStatus || "-"}
                                                                </td>
                                                            </tr>
                                                        </>
                                                    );
                                                })}
                                            </>
                                        ) : (
                                            <>
                                                <tr>
                                                    <td colSpan={8} className="text-center">

                                                        <h5 className="my-3">NO RECORD FOUND</h5>
                                                    </td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewProduct;
